import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  MapPin,
  Phone,
  Mail,
  User,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import GoogleMapLocationPicker from "@/components/GoogleMapLocationPicker";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function BusinessApprovalModule({ onBadgeUpdate }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Form states
  const [editedName, setEditedName] = useState("");
  const [editedCity, setEditedCity] = useState("");
  const [editedDistrict, setEditedDistrict] = useState("");
  const [editedAddress, setEditedAddress] = useState("");
  const [editedLat, setEditedLat] = useState("");
  const [editedLng, setEditedLng] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchBusinesses();
  }, [activeTab]);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${API}/admin/businesses?status=${activeTab}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
      }
    } catch (error) {
      toast.error(t("common:businessesLoadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await fetch(`${API}/admin/businesses/${selectedBusiness.business_id}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editedName || selectedBusiness.name,
          city: editedCity || selectedBusiness.city,
          district: editedDistrict || selectedBusiness.district,
          address: editedAddress || selectedBusiness.address,
          latitude: editedLat ? parseFloat(editedLat) : selectedBusiness.latitude || selectedBusiness.lat,
          longitude: editedLng ? parseFloat(editedLng) : selectedBusiness.longitude || selectedBusiness.lng,
          note: approvalNote,
        }),
      });

      if (response.ok) {
        toast.success(t("admin:businessApproved"));
        // Optimistic update - immediately update local state
        setBusinesses(prev => prev.filter(b => b.business_id !== selectedBusiness.business_id));
        setShowApproveModal(false);
        setSelectedBusiness(null);
        resetEditForm();
        // Also refresh from server
        fetchBusinesses();
        // Sidebar badge'ini güncelle
        if (onBadgeUpdate) onBadgeUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:approvalFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const resetEditForm = () => {
    setEditedName("");
    setEditedCity("");
    setEditedDistrict("");
    setEditedAddress("");
    setEditedLat("");
    setEditedLng("");
    setApprovalNote("");
  };

  const handleReject = async () => {
    if (!selectedBusiness || !rejectReason.trim()) return;
    
    try {
      const response = await fetch(`${API}/admin/businesses/${selectedBusiness.business_id}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          reason: rejectReason,
        }),
      });

      if (response.ok) {
        toast.success(t("admin:businessRejected"));
        setShowRejectModal(false);
        setSelectedBusiness(null);
        setRejectReason("");
        fetchBusinesses();
        // Sidebar badge'ini güncelle
        if (onBadgeUpdate) onBadgeUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:rejectionFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const openApproveModal = (business) => {
    setSelectedBusiness(business);
    setEditedName(business.name);
    setEditedCity(business.city || "");
    setEditedDistrict(business.district || "");
    setEditedAddress(business.address || "");
    setEditedLat(business.latitude || business.lat || "");
    setEditedLng(business.longitude || business.lng || "");
    setApprovalNote("");
    setShowApproveModal(true);
  };

  const openRejectModal = (business) => {
    setSelectedBusiness(business);
    setRejectReason("");
    setShowRejectModal(true);
  };
  
  const openDetailModal = (business) => {
    setSelectedBusiness(business);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Beklemede</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Onaylı</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Reddedildi</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const pendingCount = businesses.filter(b => b.approval_status === "pending").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Building2 className="w-7 h-7" />
            İşletme Onayları
          </h2>
          <p className="text-grey mt-1">{t("admin:yeniIsletmeBasvurulariniInceleyinVe")}</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-yellow-500 text-white text-lg px-4 py-2">
            {pendingCount} Bekleyen
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Bekleyen
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Onaylı
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Reddedilen
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto" />
              </CardContent>
            </Card>
          ) : businesses.length > 0 ? (
            <Card className="border-0 shadow-sm">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">{t("common:business")}</TableHead>
                      <TableHead className="whitespace-nowrap">Sahip</TableHead>
                      <TableHead className="whitespace-nowrap">{t('business:location')}</TableHead>
                      <TableHead className="whitespace-nowrap">{t('members:registrationDate')}</TableHead>
                      <TableHead className="whitespace-nowrap">{t('common:status')}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">{t('common:actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {businesses.map((business) => (
                      <TableRow key={business.business_id}>
                        <TableCell className="whitespace-nowrap">
                          <div>
                            <p className="font-medium">{business.name}</p>
                            <p className="text-xs text-grey flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {business.phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {(business.owner_name || business.owner) ? (
                            <div>
                              <p className="text-sm font-medium">
                                {business.owner_name || (business.owner ? `${business.owner.name} ${business.owner.surname}` : '-')}
                              </p>
                              <p className="text-xs text-grey flex items-center gap-1">
                                <Mail className="w-3 h-3" /> 
                                {business.owner_email || (business.owner ? business.owner.email : '-')}
                              </p>
                            </div>
                          ) : (
                            <span className="text-grey text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <p className="text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> 
                            {business.district}, {business.city}
                          </p>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {business.created_at && (
                            <span className="text-sm">
                              {format(new Date(business.created_at), "dd/MM/yyyy", { locale: getDateLocale() })}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {getStatusBadge(business.approval_status)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailModal(business)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Detay
                            </Button>
                            {business.approval_status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => openApproveModal(business)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Onayla
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openRejectModal(business)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reddet
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Building2 className="w-12 h-12 text-grey mx-auto mb-3" />
                <p className="text-grey">
                  {activeTab === "pending" && t("common:noPendingBusinesses")}
                  {activeTab === "approved" && t("admin:noApprovedBusinesses")}
                  {activeTab === "rejected" && t("admin:noRejectedBusinesses")}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              İşletmeyi Onayla - Konum Doğrulama
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedBusiness && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-800">
                    <strong>{selectedBusiness.name}</strong> işletmesini onaylamak üzeresiniz.
                    Haritadan konumu doğrulayın veya düzeltin.
                  </p>
                </div>
                
                {/* Owner Info in Approval Modal */}
                {(selectedBusiness.owner_name || selectedBusiness.owner) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {(selectedBusiness.owner_name || selectedBusiness.owner?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Talep Eden</p>
                      <p className="text-sm font-medium text-blue-900">
                        {selectedBusiness.owner_name || (selectedBusiness.owner ? `${selectedBusiness.owner.name} ${selectedBusiness.owner.surname}` : '-')}
                        <span className="text-blue-600 font-normal ml-2">
                          ({selectedBusiness.owner_email || (selectedBusiness.owner ? selectedBusiness.owner.email : '')})
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('settings:businessName')}</Label>
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder={t("common:businessName")}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>{t('common:city')}</Label>
                      <Input
                        value={editedCity}
                        onChange={(e) => setEditedCity(e.target.value)}
                        placeholder={t('common:city')}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>{t('common:district')}</Label>
                      <Input
                        value={editedDistrict}
                        onChange={(e) => setEditedDistrict(e.target.value)}
                        placeholder={t('common:district')}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                {/* Harita ile Konum Seçimi */}
                <div className="border rounded-xl p-4 bg-slate-50">
                  <Label className="mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Konum Doğrulama (Haritadan seçin veya arayın)
                  </Label>
                  <GoogleMapLocationPicker
                    onLocationSelect={(location) => {
                      setEditedLat(location.lat);
                      setEditedLng(location.lng);
                      setEditedAddress(location.formatted_address || editedAddress);
                      if (location.district) setEditedDistrict(location.district);
                      if (location.city) setEditedCity(location.city);
                    }}
                    initialLocation={{
                      lat: parseFloat(editedLat) || parseFloat(selectedBusiness.latitude) || parseFloat(selectedBusiness.lat) || 41.0082,
                      lng: parseFloat(editedLng) || parseFloat(selectedBusiness.longitude) || parseFloat(selectedBusiness.lng) || 28.9784,
                    }}
                    initialAddress={editedAddress || selectedBusiness.address || ""}
                    height="300px"
                  />
                </div>

                <div>
                  <Label>{t('common:address')}</Label>
                  <Textarea
                    value={editedAddress}
                    onChange={(e) => setEditedAddress(e.target.value)}
                    placeholder="Tam adres"
                    className="rounded-xl"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("common:latitude")}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedLat}
                      onChange={(e) => setEditedLat(e.target.value)}
                      placeholder={selectedBusiness.latitude || selectedBusiness.lat || "Enlem"}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>{t("common:longitude")}</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedLng}
                      onChange={(e) => setEditedLng(e.target.value)}
                      placeholder={selectedBusiness.longitude || selectedBusiness.lng || "Boylam"}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t("common:noteOptional")}</Label>
                  <Textarea
                    value={approvalNote}
                    onChange={(e) => setApprovalNote(e.target.value)}
                    placeholder="Onay ile ilgili not..."
                    className="rounded-xl"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setShowApproveModal(false)}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                    onClick={handleApprove}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Onayla
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              İşletmeyi Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedBusiness && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-800">
                    <strong>{selectedBusiness.name}</strong> işletmesini reddetmek üzeresiniz.
                    İşletme sahibine red sebebi ile birlikte email gönderilecek.
                  </p>
                </div>
                
                {/* Owner Info in Reject Modal */}
                {(selectedBusiness.owner_name || selectedBusiness.owner) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {(selectedBusiness.owner_name || selectedBusiness.owner?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs text-grey font-medium">{t("admin:redBildirimiGonderilecekKisi")}</p>
                      <p className="text-sm font-medium">
                        {selectedBusiness.owner_name || (selectedBusiness.owner ? `${selectedBusiness.owner.name} ${selectedBusiness.owner.surname}` : '-')}
                        <span className="text-grey font-normal ml-2">
                          ({selectedBusiness.owner_email || (selectedBusiness.owner ? selectedBusiness.owner.email : '')})
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label>{t("admin:rejectionReason")}</Label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t("admin:ph_nedenReddediliyorİsletmeSahibineGonderile")}
                    className="rounded-xl"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setShowRejectModal(false)}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 rounded-xl"
                    onClick={handleReject}
                    disabled={!rejectReason.trim()}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reddet
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              İşletme Detayları
            </DialogTitle>
          </DialogHeader>
          {selectedBusiness && (
            <div className="space-y-4 mt-4">
              {/* Business Header */}
              <div className="flex items-start gap-4">
                {selectedBusiness.images?.[0] ? (
                  <img 
                    src={selectedBusiness.images[0]} 
                    alt={selectedBusiness.name}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-navy">{selectedBusiness.name}</h3>
                  <p className="text-grey">{selectedBusiness.business_type === "individual" ? "Bireysel" : "Kurumsal"}</p>
                  {getStatusBadge(selectedBusiness.approval_status)}
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-xl p-4">
                <div>
                  <p className="text-xs text-grey">{t('common:city')}</p>
                  <p className="font-medium">{selectedBusiness.city || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-grey">{t('common:district')}</p>
                  <p className="font-medium">{selectedBusiness.district || "-"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-grey">{t('common:address')}</p>
                  <p className="font-medium">{selectedBusiness.address || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-grey">{t('common:phone')}</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {selectedBusiness.phone || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-grey">{t('common:email')}</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {selectedBusiness.email || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-grey">Koordinatlar</p>
                  <p className="font-medium text-sm">
                    {selectedBusiness.latitude?.toFixed(4)}, {selectedBusiness.longitude?.toFixed(4)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-grey">{t('members:registrationDate')}</p>
                  <p className="font-medium">
                    {selectedBusiness.created_at 
                      ? format(new Date(selectedBusiness.created_at), "dd/MM/yyyy HH:mm", { locale: getDateLocale() })
                      : "-"
                    }
                  </p>
                </div>
              </div>
              
              {/* Owner Info */}
              {(selectedBusiness.owner_name || selectedBusiness.owner) && (
                <div className="border rounded-xl p-4">
                  <p className="text-sm font-medium text-grey mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Talep Eden (İşletme Sahibi)
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center text-white font-medium">
                      {(selectedBusiness.owner_name || selectedBusiness.owner?.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">
                        {selectedBusiness.owner_name || (selectedBusiness.owner ? `${selectedBusiness.owner.name} ${selectedBusiness.owner.surname}` : '-')}
                      </p>
                      <p className="text-sm text-grey flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {selectedBusiness.owner_email || (selectedBusiness.owner ? selectedBusiness.owner.email : '-')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Branches */}
              {selectedBusiness.branches?.length > 0 && (
                <div className="border rounded-xl p-4">
                  <p className="text-sm font-medium text-grey mb-2">{t('common:branches')}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedBusiness.branches.map((branch, i) => (
                      <Badge key={i} variant="outline">{branch}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Rejection Reason - Show only for rejected businesses */}
              {selectedBusiness.approval_status === "rejected" && (
                <div className="border border-red-200 bg-red-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-red-600 mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Red Nedeni
                  </p>
                  <p className="text-red-800">
                    {selectedBusiness.rejection_reason || selectedBusiness.approval_note || t("common:notSpecified")}
                  </p>
                  {selectedBusiness.rejected_at && (
                    <p className="text-xs text-red-500 mt-2">
                      Red tarihi: {format(new Date(selectedBusiness.rejected_at), "dd/MM/yyyy HH:mm", { locale: getDateLocale() })}
                    </p>
                  )}
                </div>
              )}
              
              {/* Actions */}
              {selectedBusiness.approval_status === "pending" && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                    onClick={() => {
                      setShowDetailModal(false);
                      openApproveModal(selectedBusiness);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Onayla
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setShowDetailModal(false);
                      openRejectModal(selectedBusiness);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reddet
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
